/**
 * UnifyTable — Mantine Table wrapped in Paper with optional search, sort, pagination,
 * standard Select filters, FilterMultiSelect filters, advanced FilterListDrawer, and title/subtitle.
 *
 * **Built-in column sort** (`sortable={true}`): clicking the same column header cycles
 * ascending → descending → unsorted (third click restores the original `data` order).
 */

import { Button, Group, Paper, Select, Stack, Table, Text, Title } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconFilter, IconSelector } from '@tabler/icons-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { FilterMultiSelect, type FilterMultiSelectData } from '@/components/FilterMultiSelect';
import { FilterListDrawer, type FilterListSection } from '@/components/FilterListDrawer';
import { SearchInput } from '@/components/SearchInput';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import classes from '@/components/UnifyTable.module.css';

export interface UnifyTableColumn<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  /**
   * When the table `sortable` prop is true: set `false` to disable sorting on this column.
   * If `sortableColumnKeys` is set on the table, the column must also be listed there (unless `sortable: false`).
   * Sort cycle on repeated header clicks: ascending → descending → original order (cleared).
   */
  sortable?: boolean;
  getValue?: (row: T) => string;
  /** Full header cell content; when set, default label/sort UI is omitted (e.g. parent-controlled sort). */
  renderHeader?: () => ReactNode;
  /** Optional fixed width for consistent cross-table alignment (e.g. "180px" or 180). */
  width?: number | string;
  /** Cell/header text alignment. */
  align?: 'left' | 'center' | 'right';
  /** Sticky column pinned to the right on wide viewports; scrolls normally on narrow screens. */
  pinRight?: boolean;
}

/** Ordered subset of columns for header/body when `visibleColumnKeys` is set; else all columns. */
export function selectVisibleColumns<T>(
  columns: UnifyTableColumn<T>[],
  visibleColumnKeys?: string[],
): UnifyTableColumn<T>[] {
  if (!visibleColumnKeys) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  return visibleColumnKeys
    .map((key) => byKey.get(key))
    .filter((c): c is UnifyTableColumn<T> => c != null);
}

export interface UnifyTableFilterConfig {
  /** Stable key for React list / aria when multiple filters share the same columnKey. */
  id?: string;
  placeholder: string;
  options: { value: string; label: string }[];
  columnKey: string;
  /** Overrides placeholder for `aria-label` on the Select. */
  ariaLabel?: string;
  w?: number | string;
  /** Initial selection applied on first render (e.g. default Status to Active). */
  defaultValue?: string;
}

/** Optional toolbar **FilterMultiSelect**; filters rows where `columnKey` cell value is in the selected values. Empty selection = no rows unless `emptyMeansAll` is true. */
export interface UnifyTableMultiFilterConfig {
  /** Stable key for React list when multiple multis use the same columnKey. */
  id?: string;
  columnKey: string;
  allLabel: string;
  data: FilterMultiSelectData;
  label?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  /**
   * Row-filter behavior when the multi-select value is `[]`.
   * @default false — show zero rows when nothing is selected. Set true to show all rows (no filter).
   * Independent of FilterMultiSelect checkbox visuals (`[]` always means no selections there).
   */
  emptyMeansAll?: boolean;
  /** Initial selection applied on first render (e.g. default the filter to a value on load). */
  defaultValue?: string[];
  /** When true, selection triggers server-side filtering; skip client row filter for this control. */
  serverFiltered?: boolean;
  w?: number | string;
  clearable?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/** FilterListSection plus column key for client-side filtering on Apply */
export interface UnifyTableAdvancedFilterSection extends FilterListSection {
  columnKey: string;
}

export interface UnifyTableProps<T> {
  columns: UnifyTableColumn<T>[];
  /**
   * When set, only these column `key`s are rendered, in this order. Filtering, search, and
   * sort still resolve against the full `columns` list, so a hidden column can still drive a
   * toolbar filter. Omit to render every column in `columns`.
   */
  visibleColumnKeys?: string[];
  data: T[];
  getRowKey: (row: T) => string;
  title?: string;
  subtitle?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /**
   * Client-side sort on column headers. Same column: 1st click ascending, 2nd descending,
   * 3rd click clears sort (rows match the `data` prop order).
   */
  sortable?: boolean;
  /**
   * When `sortable` is true, only these column `key`s are sortable. Omit for “all columns except those with `sortable: false`”.
   * Pass `[]` to disable sorting on every column while keeping sort UI patterns off (rare).
   */
  sortableColumnKeys?: string[];
  pagination?: boolean;
  /** Must match a value in TABLE_ROWS_PER_PAGE_OPTIONS (10, 25, 50, 100). Default 10 */
  defaultRowsPerPage?: number;
  /**
   * Single Select filter (shorthand). Combined with `filters` as `[filter, ...filters]` when both are set.
   * Prefer `filters` when you have more than one.
   */
  filter?: UnifyTableFilterConfig;
  /** One or more Select toolbar filters (AND). Rendered in array order before `multiFilters`. */
  filters?: UnifyTableFilterConfig[];
  /**
   * Single FilterMultiSelect (shorthand). Combined with `multiFilters` as `[multiFilter, ...multiFilters]` when both are set.
   */
  multiFilter?: UnifyTableMultiFilterConfig;
  /** One or more FilterMultiSelect toolbar filters (AND). Rendered after all `filter` / `filters` entries. */
  multiFilters?: UnifyTableMultiFilterConfig[];
  /** When exactly one `multiFilter` / `multiFilters` entry exists, called with that control’s value (convenience). */
  onMultiFilterChange?: (value: string[]) => void;
  /** Called for every multi-select change with its index in the resolved multi-filter list. */
  onMultiFiltersChange?: (index: number, value: string[]) => void;
  advancedFilterSections?: UnifyTableAdvancedFilterSection[];
  onAdvancedFilterApply?: (selections: Record<string, string[]>) => void;
  /** Extra controls rendered in the toolbar after the built-in filters (e.g. a DateRangeSelect). */
  extraFilters?: ReactNode;
  onRowClick?: (row: T) => void;
  /** @default true — set false when the table sits inside an existing Card/Paper (e.g. below a custom filter toolbar). */
  withPaper?: boolean;
  /**
   * Minimum table width before horizontal scroll activates (Table.ScrollContainer).
   * Keeps wide tables usable on narrow viewports. @default 640
   */
  scrollMinWidth?: number | string;
  /** Passed to Mantine `Table` (e.g. `style`, `withTableBorder`, `highlightOnHover`). */
  tableProps?: ComponentPropsWithoutRef<typeof Table>;
  /** Content rendered in the top-right of the header area (e.g. an "Add" button). Only visible when `title` or `subtitle` is set. */
  headerRight?: ReactNode;
  /** When true, the advanced Filters button renders in the header row (before `headerRight`) instead of the toolbar. */
  advancedFiltersInHeader?: boolean;
  /** Body text when no rows match the current search/filters. @default 'No results found' */
  emptyMessage?: string;
}

function defaultGetValue<T>(row: T, key: string): string {
  const v = (row as Record<string, unknown>)[key];
  if (v == null) return '';
  return String(v);
}

function getColumnValue<T>(col: UnifyTableColumn<T>, row: T): string {
  return col.getValue ? col.getValue(row) : defaultGetValue(row, col.key);
}

const ALLOWED_ROWS_PER_PAGE = new Set(['10', '25', '50', '100']);

const DEFAULT_PINNED_COLUMN_WIDTH = 140;

function resolveColumnWidth<T>(col: UnifyTableColumn<T>): number | string | undefined {
  if (col.width != null) return col.width;
  if (col.pinRight) return DEFAULT_PINNED_COLUMN_WIDTH;
  return undefined;
}

/** Pass the rendered columns, not every defined column, so hidden columns don't force scroll. */
export function resolveScrollMinWidth<T>(
  renderColumns: UnifyTableColumn<T>[],
  scrollMinWidth: number | string,
): number | string {
  const hasPinned = renderColumns.some((col) => col.pinRight);
  if (!hasPinned) return scrollMinWidth;

  const base = typeof scrollMinWidth === 'number' ? scrollMinWidth : 640;
  return Math.max(base, renderColumns.length * 130);
}

/**
 * Sort icons for column headers. Use with custom `renderHeader` when the parent owns sort state
 * so indicators match built-in UnifyTable headers (Tabler chevrons / selector).
 * Parent sort handlers should match the built-in cycle: asc → desc → cleared (third click).
 */
export function UnifyTableSortAffordance({
  active,
  direction,
}: {
  active: boolean;
  direction: 'asc' | 'desc';
}): JSX.Element {
  return (
    <Text component="span" c="dimmed" style={{ display: 'inline-flex' }}>
      {!active && <IconSelector size={14} />}
      {active && direction === 'asc' && <IconChevronUp size={14} />}
      {active && direction === 'desc' && <IconChevronDown size={14} />}
    </Text>
  );
}

export function UnifyTable<T>({
  columns,
  visibleColumnKeys,
  data,
  getRowKey,
  title,
  subtitle,
  searchable,
  searchPlaceholder = 'Search…',
  sortable: sortableEnabled,
  sortableColumnKeys,
  pagination,
  defaultRowsPerPage = 10,
  filter,
  filters,
  multiFilter,
  multiFilters,
  onMultiFilterChange,
  onMultiFiltersChange,
  advancedFilterSections,
  onAdvancedFilterApply,
  extraFilters,
  onRowClick,
  withPaper = true,
  scrollMinWidth = 640,
  tableProps,
  headerRight,
  advancedFiltersInHeader,
  emptyMessage = 'No results found',
}: UnifyTableProps<T>): JSX.Element {
  const { className: tableClassName, ...restTableProps } = tableProps ?? {};
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /** Columns actually rendered: ordered subset when `visibleColumnKeys` is set, else all. */
  const renderColumns = useMemo(
    () => selectVisibleColumns(columns, visibleColumnKeys),
    [columns, visibleColumnKeys],
  );
  const resolvedSelectFilters = useMemo(() => {
    const list = filters ?? [];
    return filter ? [filter, ...list] : list;
  }, [filter, filters]);

  const resolvedMultiFilters = useMemo(() => {
    const list = multiFilters ?? [];
    return multiFilter ? [multiFilter, ...list] : list;
  }, [multiFilter, multiFilters]);

  const [selectValues, setSelectValues] = useState<(string | null)[]>(() =>
    resolvedSelectFilters.map((cfg) => cfg.defaultValue ?? null),
  );
  const [multiFilterValues, setMultiFilterValues] = useState<string[][]>(() =>
    resolvedMultiFilters.map((cfg) => cfg.defaultValue ?? []),
  );
  const [advancedDrawerOpen, setAdvancedDrawerOpen] = useState(false);
  const [appliedAdvanced, setAppliedAdvanced] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    ALLOWED_ROWS_PER_PAGE.has(String(defaultRowsPerPage)) ? String(defaultRowsPerPage) : '10',
  );

  const selectFilterCount = resolvedSelectFilters.length;
  const multiFilterCount = resolvedMultiFilters.length;

  useEffect(() => {
    setSelectValues((prev) => {
      if (prev.length === selectFilterCount) return prev;
      if (selectFilterCount > prev.length) {
        return [
          ...prev,
          ...Array.from(
            { length: selectFilterCount - prev.length },
            (_, k) => resolvedSelectFilters[prev.length + k]?.defaultValue ?? null,
          ),
        ];
      }
      return prev.slice(0, selectFilterCount);
    });
  }, [selectFilterCount, resolvedSelectFilters]);

  useEffect(() => {
    setMultiFilterValues((prev) => {
      if (prev.length === multiFilterCount) return prev;
      if (multiFilterCount > prev.length) {
        return [
          ...prev,
          ...Array.from(
            { length: multiFilterCount - prev.length },
            (_, k) => resolvedMultiFilters[prev.length + k]?.defaultValue ?? [],
          ),
        ];
      }
      return prev.slice(0, multiFilterCount);
    });
  }, [multiFilterCount, resolvedMultiFilters]);

  const hasAdvancedFilters = Boolean(advancedFilterSections && advancedFilterSections.length > 0);
  const advancedFiltersInToolbar = hasAdvancedFilters && !advancedFiltersInHeader;

  const showToolbar = Boolean(
    searchable ||
    selectFilterCount > 0 ||
    multiFilterCount > 0 ||
    advancedFiltersInToolbar ||
    extraFilters,
  );

  const resolvedScrollMinWidth = useMemo(
    () => resolveScrollMinWidth(renderColumns, scrollMinWidth),
    [renderColumns, scrollMinWidth],
  );

  const columnIsSortable = useCallback(
    (col: UnifyTableColumn<T>) => {
      if (col.renderHeader) return false;
      if (!sortableEnabled || col.sortable === false) return false;
      if (sortableColumnKeys != null) return sortableColumnKeys.includes(col.key);
      return true;
    },
    [sortableEnabled, sortableColumnKeys],
  );

  /** Cycles: asc → desc → clear (third click restores unsorted / `data` order). */
  const handleSort = useCallback(
    (key: string) => {
      const col = columns.find((c) => c.key === key);
      if (!col || !columnIsSortable(col)) return;
      if (sortKey !== key) {
        setSortKey(key);
        setSortDir('asc');
        return;
      }
      if (sortDir === 'asc') {
        setSortDir('desc');
        return;
      }
      // Third click on same column (or any click after desc): clear sort
      setSortKey(null);
      setSortDir('asc');
    },
    [columns, sortKey, sortDir, columnIsSortable],
  );

  const filtered = useMemo(() => {
    let rows = [...data];

    if (searchable && search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((row) =>
        columns.some((col) => getColumnValue(col, row).toLowerCase().includes(q)),
      );
    }

    resolvedSelectFilters.forEach((cfg, i) => {
      const v = selectValues[i];
      if (v == null || v === '') return;
      const col = columns.find((c) => c.key === cfg.columnKey);
      if (col) {
        rows = rows.filter((row) => getColumnValue(col, row) === v);
      }
    });

    resolvedMultiFilters.forEach((cfg, i) => {
      if (cfg.serverFiltered) return;
      const vals = multiFilterValues[i] ?? [];
      const col = columns.find((c) => c.key === cfg.columnKey);
      if (!col) return;
      const emptyMeansAll = cfg.emptyMeansAll === true;
      if (vals.length === 0) {
        if (!emptyMeansAll) {
          rows = [];
        }
      } else {
        rows = rows.filter((row) => vals.includes(getColumnValue(col, row)));
      }
    });

    if (advancedFilterSections?.length) {
      for (const section of advancedFilterSections) {
        const selected = appliedAdvanced[section.label];
        if (selected && selected.length > 0) {
          const col = columns.find((c) => c.key === section.columnKey);
          if (col) {
            rows = rows.filter((row) => selected.includes(getColumnValue(col, row)));
          }
        }
      }
    }

    if (sortableEnabled && sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      if (col && columnIsSortable(col)) {
        rows.sort((a, b) => {
          const va = getColumnValue(col, a);
          const vb = getColumnValue(col, b);
          const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return rows;
  }, [
    data,
    columns,
    searchable,
    search,
    resolvedSelectFilters,
    selectValues,
    resolvedMultiFilters,
    multiFilterValues,
    advancedFilterSections,
    appliedAdvanced,
    sortableEnabled,
    sortKey,
    sortDir,
    columnIsSortable,
  ]);

  const itemsPerPage = parseInt(rowsPerPage, 10);
  const paginatedRows = useMemo(() => {
    if (!pagination) return filtered;
    const start = (page - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, pagination, page, itemsPerPage]);

  const handleAdvancedApply = useCallback(
    (selections: Record<string, string[]>) => {
      setAppliedAdvanced(selections);
      setPage(1);
      onAdvancedFilterApply?.(selections);
    },
    [onAdvancedFilterApply],
  );

  const advancedFilterControls = hasAdvancedFilters ? (
    <>
      <Button
        variant="outline"
        leftSection={<IconFilter size={16} />}
        onClick={() => setAdvancedDrawerOpen(true)}
      >
        Filters
      </Button>
      <FilterListDrawer
        opened={advancedDrawerOpen}
        onClose={() => setAdvancedDrawerOpen(false)}
        sections={advancedFilterSections!.map(({ columnKey: _c, ...rest }) => rest)}
        onApply={handleAdvancedApply}
        onClear={() => {
          setAppliedAdvanced({});
          setPage(1);
        }}
      />
    </>
  ) : null;

  const inner = (
    <>
      {(title || subtitle || headerRight || (advancedFiltersInHeader && hasAdvancedFilters)) && (
        <Group justify="space-between" align="flex-start" mb={showToolbar ? 'md' : 'sm'} wrap="wrap" gap="md">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Title order={4} fw={600}>
                {title}
              </Title>
            )}
            {subtitle && (
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            )}
          </Stack>
          {(advancedFiltersInHeader && advancedFilterControls) || headerRight ? (
            <Group gap="sm" wrap="nowrap">
              {advancedFiltersInHeader && advancedFilterControls}
              {headerRight}
            </Group>
          ) : null}
        </Group>
      )}

      {showToolbar && (
        <Group gap="md" wrap="wrap" mb="md" align="flex-end" className={classes.toolbar}>
          {searchable && (
            <SearchInput
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
                setPage(1);
              }}
              className={classes.searchInput}
            />
          )}
          {resolvedSelectFilters.map((cfg, i) => (
            <Select
              key={cfg.id ?? `unify-select-${cfg.columnKey}-${i}`}
              placeholder={cfg.placeholder}
              data={cfg.options}
              value={selectValues[i] ?? null}
              onChange={(v) => {
                setSelectValues((prev) => {
                  const next = [...prev];
                  next[i] = v;
                  return next;
                });
                setPage(1);
              }}
              clearable
              className={cfg.w ? undefined : classes.toolbarControl}
              w={cfg.w}
              aria-label={cfg.ariaLabel ?? cfg.placeholder}
            />
          ))}
          {resolvedMultiFilters.map((cfg, i) => (
            <FilterMultiSelect
              key={cfg.id ?? `unify-multi-${cfg.columnKey}-${i}`}
              allLabel={cfg.allLabel}
              data={cfg.data}
              value={multiFilterValues[i] ?? []}
              onChange={(v) => {
                setMultiFilterValues((prev) => {
                  const next = [...prev];
                  next[i] = v;
                  return next;
                });
                setPage(1);
                onMultiFiltersChange?.(i, v);
                if (multiFilterCount === 1) {
                  onMultiFilterChange?.(v);
                }
              }}
              label={cfg.label}
              placeholder={cfg.placeholder}
              searchPlaceholder={cfg.searchPlaceholder}
              className={cfg.w ? undefined : classes.toolbarControl}
              w={cfg.w}
              clearable={cfg.clearable}
              size={cfg.size}
            />
          ))}
          {extraFilters}
          {advancedFiltersInToolbar && advancedFilterControls}
        </Group>
      )}

      <Table.ScrollContainer minWidth={resolvedScrollMinWidth} type="native" className={classes.scrollContainer}>
        <Table highlightOnHover {...restTableProps} className={tableClassName}>
        <Table.Thead>
          <Table.Tr>
            {renderColumns.map((col) => {
              const canSort = columnIsSortable(col);
              const active = sortKey === col.key;
              const columnWidth = resolveColumnWidth(col);
              const thStyle: CSSProperties = {
                width: columnWidth,
                minWidth: columnWidth,
                textAlign: col.align,
              };
              if (col.renderHeader) {
                return (
                  <Table.Th
                    key={col.key}
                    style={thStyle}
                    className={col.pinRight ? classes.pinnedRightHeader : undefined}
                  >
                    {col.renderHeader()}
                  </Table.Th>
                );
              }
              return (
                <Table.Th
                  key={col.key}
                  onClick={() => canSort && handleSort(col.key)}
                  style={
                    canSort
                      ? { ...thStyle, cursor: 'pointer', userSelect: 'none' }
                      : thStyle
                  }
                  className={col.pinRight ? classes.pinnedRightHeader : undefined}
                >
                  <Group
                    gap={6}
                    wrap="nowrap"
                    justify={
                      col.align === 'right'
                        ? 'flex-end'
                        : col.align === 'center'
                          ? 'center'
                          : 'flex-start'
                    }
                  >
                    <Text component="span" size="sm" fw={600}>
                      {col.label}
                    </Text>
                    {canSort && <UnifyTableSortAffordance active={active} direction={sortDir} />}
                  </Group>
                </Table.Th>
              );
            })}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginatedRows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={renderColumns.length}>
                <Text size="sm" c="dimmed" ta="center" py="md">
                  {emptyMessage}
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            paginatedRows.map((row) => (
              <Table.Tr
                key={getRowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                    : undefined
                }
              >
                {renderColumns.map((col) => {
                  const columnWidth = resolveColumnWidth(col);
                  return (
                  <Table.Td
                    key={col.key}
                    className={col.pinRight ? classes.pinnedRightCell : undefined}
                    style={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      textAlign: col.align,
                    }}
                  >
                    {col.render ? col.render(row) : <Text size="sm">{getColumnValue(col, row)}</Text>}
                  </Table.Td>
                  );
                })}
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {pagination && (
        <TablePaginationFooter
          totalItems={filtered.length}
          currentPage={page}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(v) => v != null && setRowsPerPage(v)}
        />
      )}
    </>
  );

  if (withPaper) {
    return (
      <Paper withBorder p="lg" radius="md" w="100%" className={classes.paperRoot}>
        {inner}
      </Paper>
    );
  }

  return inner;
}
